import { useState, useMemo } from 'react';
import {
  Box,
  Heading,
  HStack,
  Select,
  Text,
  VStack,
  useColorModeValue,
} from '@chakra-ui/react';
import { Link } from 'react-router-dom';
import { FiMessageSquare } from 'react-icons/fi';
import { useAllSupportTickets } from '../../hooks/useAdmin';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { EmptyState } from '../common/EmptyState';
import { StatusBadge } from '../common/StatusBadge';
import { formatRelativeTime } from '../../utils/formatters';

export default function AdminSupport() {
  const { tickets, loading } = useAllSupportTickets();
  const [statusFilter, setStatusFilter] = useState('all');

  const bg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return tickets;
    return tickets.filter((t) => t.status === statusFilter);
  }, [tickets, statusFilter]);

  if (loading) return <LoadingSpinner message="Loading tickets..." />;

  return (
    <VStack spacing={6} align="stretch">
      <HStack justify="space-between" align="center">
        <Box>
          <Heading size="lg">Support Tickets</Heading>
          <Text color="gray.500" mt={1}>{tickets.length} total tickets</Text>
        </Box>
        <Select maxW="180px" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="all">All</option>
          <option value="open">Open</option>
          <option value="resolved">Resolved</option>
          <option value="closed">Closed</option>
        </Select>
      </HStack>

      {filtered.length === 0 ? (
        <EmptyState
          title="No tickets found"
          description="Support tickets will appear here."
          icon={FiMessageSquare}
        />
      ) : (
        <VStack spacing={3} align="stretch">
          {filtered.map((ticket) => (
            <Box
              key={ticket.id}
              as={Link}
              to={`/admin/support/${ticket.id}`}
              bg={bg}
              rounded="lg"
              borderWidth={1}
              borderColor={borderColor}
              p={4}
              _hover={{ borderColor: 'orange.300', shadow: 'sm' }}
              transition="all 0.2s"
              display="block"
            >
              <HStack justify="space-between" align="start" mb={2}>
                <Text fontWeight="semibold" fontSize="md" noOfLines={1} flex={1}>
                  {ticket.subject}
                </Text>
                <StatusBadge status={ticket.status} />
              </HStack>
              <Text fontSize="sm" color="gray.500" noOfLines={2} mb={2}>
                {ticket.message}
              </Text>
              <HStack spacing={3} fontSize="xs" color="gray.400">
                <Text>From: {ticket.creatorUsername}</Text>
                <Text>{formatRelativeTime(ticket.createdAt)}</Text>
              </HStack>
            </Box>
          ))}
        </VStack>
      )}
    </VStack>
  );
}
